import React, { useState, useEffect, memo, useCallback, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import { logger } from '../utils/logger';
import { sendDirectEmail } from '../utils/emailSharing';

const InvitationManager = memo(({ onInvitationCreated }) => {
  const { user, userProfile, inviteChild, getPendingInvitations, cancelInvitation, error } = useUser();
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [copiedToken, setCopiedToken] = useState(false);
  
  // Lazy load invitationService to avoid static import conflict
  const invitationServiceRef = useRef(null);
  const getInvitationService = useCallback(async () => {
    if (!invitationServiceRef.current) {
      invitationServiceRef.current = (await import('../utils/invitationService')).default;
    }
    return invitationServiceRef.current;
  }, []);

  // Define loadPendingInvitations BEFORE useEffect that uses it
  const loadPendingInvitations = useCallback(async () => {
    setIsLoading(true);
    try {
      const invitations = await getPendingInvitations();
      setPendingInvitations(invitations);
    } catch (error) {
      logger.error('Error loading pending invitations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getPendingInvitations]);

  useEffect(() => {
    const email = userProfile?.email || user?.email;
    if (email) {
      logger.info('[InvitationManager] Loading pending invitations for:', email);
      loadPendingInvitations();
    }
  }, [userProfile?.email, user?.email, loadPendingInvitations]);

  const handleGenerateInvitation = async () => {
    // Try to get email from userProfile or user
    const parentEmail = userProfile?.email || user?.email;
    
    if (!parentEmail) {
      logger.error('[InvitationManager] No email available for invitation');
      alert('Unable to generate invitation: Please ensure you are logged in and your profile is loaded.');
      return;
    }

    setIsGenerating(true);
    setGeneratedToken(null);
    setCopiedToken(false);

    try {
      logger.info('[InvitationManager] Generating invitation for:', parentEmail);
      logger.info('[InvitationManager] Calling inviteChild...');
      
      const result = await inviteChild(parentEmail, null, {
        expirationDays: 7
      });

      logger.info('[InvitationManager] inviteChild returned:', result);

      if (result && result.success) {
        logger.info('[InvitationManager] Invitation created successfully, token:', result.token);
        const invitationService = await getInvitationService();
        const invitationUrl = invitationService.buildInvitationUrl(result.token);
        setGeneratedToken(invitationUrl); // Store the full URL instead
        logger.info('[InvitationManager] Invitation URL:', invitationUrl);
        
        await loadPendingInvitations();
        logger.info('[InvitationManager] Pending invitations reloaded');
        
        if (onInvitationCreated) {
          onInvitationCreated(result);
        }
      } else {
        logger.warn('[InvitationManager] Invitation creation returned unsuccessful result:', result);
        alert(result?.error || 'Failed to create invitation. Please try again.');
      }
    } catch (error) {
      logger.error('[InvitationManager] Error generating invitation:', error);
      logger.error('[InvitationManager] Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      alert(`Error creating invitation: ${error?.message || 'Unknown error. Please try again.'}`);
    } finally {
      logger.info('[InvitationManager] Setting isGenerating to false');
      setIsGenerating(false);
    }
  };

  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    } catch (error) {
      logger.error('Error copying to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    }
  };
  
  // Helper to build invitation URL
  const buildInvitationUrl = useCallback(async (token) => {
    const invitationService = await getInvitationService();
    return invitationService.buildInvitationUrl(token);
  }, [getInvitationService]);

  // Helper to open user's email client with a pre-filled invitation email
  const handleSendEmail = useCallback((invitationUrl) => {
    if (!invitationUrl) {
      logger.warn('[InvitationManager] handleSendEmail called without invitationUrl');
      return;
    }

    const subject = `Emmy's Learning Adventure – Parent Invitation Link`;
    const bodyLines = [
      `Hi there,`,
      ``,
      `Here is your invitation link to connect to the Emmy's Learning Adventure account:`,
      invitationUrl,
      ``,
      `Click the link above to sign in (or create an account) and you'll be connected to your parent dashboard.`,
      ``,
      `If you did not expect this email, you can ignore it.`
    ];

    const body = bodyLines.join('\n');

    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    try {
      window.location.href = mailtoUrl;
    } catch (err) {
      logger.error('[InvitationManager] Error opening mail client for invitation email:', err);
      alert('Unable to open your email app. Please copy the link instead.');
    }
  }, []);

  // Helper to send invitation email directly through the app (EmailJS)
  const handleSendEmailViaApp = useCallback(async (invitationUrl) => {
    if (!invitationUrl) {
      logger.warn('[InvitationManager] handleSendEmailViaApp called without invitationUrl');
      return;
    }

    const toEmail = window.prompt('Enter the email address to send this invitation to:');
    if (!toEmail || !toEmail.trim()) {
      return;
    }

    const subject = `Emmy's Learning Adventure – Parent Invitation Link`;
    const bodyLines = [
      `Hi there,`,
      ``,
      `Here is your invitation link to connect to the Emmy's Learning Adventure account:`,
      invitationUrl,
      ``,
      `Click the link above to sign in (or create an account) and you'll be connected to your parent dashboard.`,
      ``,
      `If you did not expect this email, you can ignore it.`
    ];
    const body = bodyLines.join('\n');

    try {
      const result = await sendDirectEmail({ toEmail: toEmail.trim(), subject, body });
      logger.info('[InvitationManager] Direct invitation email sent:', result);
      alert('Invitation email sent!');
    } catch (error) {
      logger.error('[InvitationManager] Error sending direct invitation email:', error);
      alert(error.message || 'Unable to send email from the app. Please try again or use the Copy Link option.');
    }
  }, []);

  const handleCancelInvitation = async (invitationId) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      await cancelInvitation(invitationId);
      await loadPendingInvitations();
    } catch (error) {
      logger.error('Error cancelling invitation:', error);
    }
  };

  const formatExpirationDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Invite Your Child 👨‍👩‍👧‍👦</h2>
          <p className="text-sm text-gray-600 mt-1">
            Generate an invitation link for your child to connect their account
          </p>
        </div>
        <button
          onClick={handleGenerateInvitation}
          disabled={isGenerating || !(userProfile?.email || user?.email)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          title={!(userProfile?.email || user?.email) ? 'Please wait for your profile to load' : 'Generate invitation link'}
        >
          {isGenerating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Generating...</span>
            </>
          ) : (
            <>
              <span>➕</span>
              <span>New Invitation</span>
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
          <div className="flex items-center">
            <span className="text-xl mr-2">⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Generated Invitation URL */}
      {generatedToken && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <span>✅</span>
                <span>Invitation Created!</span>
              </h3>
              <p className="text-sm text-green-800 mb-3">
                Share this link with your child. It will expire in 7 days.
              </p>
              <div className="bg-white rounded border border-green-300 p-3 mb-3">
                <code className="text-sm text-gray-800 break-all">{generatedToken}</code>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => handleCopyToClipboard(generatedToken)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2"
                >
                  {copiedToken ? (
                    <>
                      <span>✓</span>
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <span>📋</span>
                      <span>Copy Link</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleSendEmailViaApp(generatedToken)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2"
                >
                  <span>📨</span>
                  <span>Send via App</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSendEmail(generatedToken)}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2"
                >
                  <span>📧</span>
                  <span>Open Email App</span>
                </button>
              </div>
            </div>
            <button
              onClick={() => setGeneratedToken(null)}
              className="text-green-600 hover:text-green-800 ml-2"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Pending Invitations List */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Invitations</h3>
        
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Loading invitations...</p>
          </div>
        ) : pendingInvitations.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <div className="text-4xl mb-2">📭</div>
            <p className="text-gray-600">No pending invitations</p>
            <p className="text-sm text-gray-500 mt-1">
              Click "New Invitation" to create one
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🔗</span>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                        {invitation.invitation_token.substring(0, 16)}...
                      </code>
                      <span className={`text-xs px-2 py-1 rounded ${
                        invitation.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {invitation.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>Expires: {formatExpirationDate(invitation.expires_at)}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Created: {formatExpirationDate(invitation.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        const url = await buildInvitationUrl(invitation.invitation_token);
                        handleCopyToClipboard(url);
                      }}
                      className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                      title="Copy invitation link"
                    >
                      📋 Copy
                    </button>
                    <button
                      onClick={async () => {
                        const url = await buildInvitationUrl(invitation.invitation_token);
                        handleSendEmailViaApp(url);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      title="Send invitation email from app"
                    >
                      📨 App Email
                    </button>
                    <button
                      onClick={async () => {
                        const url = await buildInvitationUrl(invitation.invitation_token);
                        handleSendEmail(url);
                      }}
                      className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                      title="Send invitation email"
                    >
                      📧 Email
                    </button>
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                      title="Cancel invitation"
                    >
                      ✕ Cancel
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <span>ℹ️</span>
          <span>How it works:</span>
        </h4>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>Click "New Invitation" to generate a unique invitation link</li>
          <li>Share the link with your child (via email, text, etc.)</li>
          <li>Your child clicks the link and signs in (or creates an account)</li>
          <li>They'll automatically be connected to your parent account</li>
          <li>You'll see their progress in your Parent Dashboard</li>
        </ol>
      </div>
    </div>
  );
});

InvitationManager.displayName = 'InvitationManager';

export default InvitationManager;

